import { useCallback, useEffect, useMemo, useState } from "react"
import { useShallow } from "zustand/react/shallow"

import type { VehicleWithPrice } from "@be-miniapp/api"
import type { SSERideStatus } from "@be-miniapp/types"
import { useElementSize } from "@be-miniapp/ui/hooks/use-element-size"

import {
  selectRideLiveSession,
  selectRideLiveSessionActions,
  selectRidePickupDropoff,
} from "../stores/ride-data.selectors"
import { parseRideInTripRouteState } from "./ride-route-entry"
import { isPerfectRideActive } from "./ride-session"
import type { RideDataStoreHook } from "./use-fare-page-model"
import type { RideMapRefLike, UseRideMapBoundsOptions } from "./use-map-bounds"
import type {
  UseRideNavigationOptions,
  UseRideNavigationResult,
} from "./use-ride-navigation"
import {
  useRideSessionController,
  type RideSessionControllerDeps,
  type RideSessionControllerUI,
} from "./use-ride-session-controller"

export interface UseInTripPageViewModelParams {
  useRideDataStore: RideDataStoreHook
  location: { state?: unknown }
  sseData: SSERideStatus | null
  useRideNavigation: (
    opts: UseRideNavigationOptions
  ) => Pick<
    UseRideNavigationResult,
    "routeUserDriver" | "routePerfectRide" | "routeDriverDropoff"
  >
  useMapBounds: (opts: UseRideMapBoundsOptions) => {
    tryFitBound: (targetMapRef?: RideMapRefLike | null) => void
  }
  sessionControllerDeps: RideSessionControllerDeps
  ui: RideSessionControllerUI
}

/**
 * Shared in-trip screen composition: store slices, session controller, routes, map fit, sheet sizing.
 * Apps keep SSE wiring, chat notifications, `RideMap` props (e.g. Zalo marker icon), and layout.
 */
export function useInTripPageViewModel(params: UseInTripPageViewModelParams) {
  const {
    useRideDataStore,
    location,
    sseData,
    useRideNavigation,
    useMapBounds,
    sessionControllerDeps,
    ui,
  } = params

  const { sessionId: sessionIdFromLocation, vehicleData } = useMemo(
    () => parseRideInTripRouteState(location?.state),
    [location?.state]
  )

  const { pickup, dropoff } = useRideDataStore(
    useShallow(selectRidePickupDropoff)
  )
  const {
    engagement,
    state: currentState,
    sessionId: sessionIdFromState,
    lastMsgId,
  } = useRideDataStore(useShallow(selectRideLiveSession))
  const { setEngagement, setCurrentState, setLastMsgId, resetRideState } =
    useRideDataStore(useShallow(selectRideLiveSessionActions))

  const [isMapReady, setIsMapReady] = useState(false)
  const [, setAllowFetchRideSession] = useState(true)
  const [mapRef, setMapRef] = useState<RideMapRefLike | null>(null)

  const isPerfectRide = useMemo(
    () => isPerfectRideActive(engagement),
    [engagement]
  )

  const { rideSession } = useRideSessionController({
    sessionIdFromLocation: sessionIdFromLocation || undefined,
    sessionIdFromState,
    currentState,
    engagement,
    setCurrentState,
    setEngagement,
    setAllowFetchRideSession,
    resetRideState,
    sseData,
    deps: sessionControllerDeps,
    ui,
  })

  const { routeUserDriver, routePerfectRide, routeDriverDropoff } =
    useRideNavigation({
      engagement,
      pickup,
      dropoff,
      currentState: currentState || "finding-driver",
    })

  const { tryFitBound } = useMapBounds({
    currentState: currentState || "finding-driver",
    pickup,
    dropoff,
    engagement,
    routeUserDriver,
    routePerfectRide,
    isMapReady,
    mapRef,
    enableAutoFocus: false,
  })

  const { ref: sheetRef, height } = useElementSize<HTMLDivElement>()

  const onMapReady = useCallback(() => {
    setIsMapReady(true)
  }, [])

  const handleFitBound = useCallback(() => {
    try {
      tryFitBound()
    } catch (err) {
      console.error("Failed to fit bounds:", err)
    }
  }, [tryFitBound])

  const handleMapRefReady = useCallback((newMapRef: RideMapRefLike | null) => {
    setMapRef(newMapRef)
  }, [])

  const currentStateString = currentState || "finding-driver"

  useEffect(() => {
    if (!isMapReady || !mapRef) return
    if (currentStateString === "finding-driver") return

    const id = setTimeout(() => {
      handleFitBound()
    }, 120)
    return () => clearTimeout(id)
  }, [isMapReady, mapRef, currentStateString, handleFitBound])

  const handleBackClick = useCallback(() => {
    ui.navigate("/ride/home", { replace: true })
  }, [ui])

  return {
    vehicleData: vehicleData as VehicleWithPrice,
    pickup,
    dropoff,
    engagement,
    currentStateString,
    lastMsgId,
    setLastMsgId,
    isPerfectRide,
    rideSession,
    routeUserDriver,
    routePerfectRide,
    routeDriverDropoff,
    height,
    sheetRef,
    onMapReady,
    handleFitBound,
    handleMapRefReady,
    handleBackClick,
  }
}
